import { useCallback, useEffect, useState } from 'react';
import type SwiperCore from 'swiper';

import { scheduleDelayedAutoplay } from '~/features/promotions/utils/schedule-delayed-autoplay';

type UseDelayedSwiperAutoplayOptions = {
  /** When false, autoplay stays stopped and any pending start is cleared. */
  isEnabled: boolean;
  /** Ms to wait after `isEnabled` (and viewport, if required) before starting autoplay. */
  startDelayMs?: number;
  /** When true, `isInViewport` must also be true before the delay countdown begins. */
  requiresViewport?: boolean;
  isInViewport?: boolean;
};

export function useDelayedSwiperAutoplay({
  isEnabled,
  isInViewport = true,
  requiresViewport = false,
  startDelayMs = 0,
}: UseDelayedSwiperAutoplayOptions) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);
  const canStartAutoplay = isEnabled && (!requiresViewport || isInViewport);

  useEffect(() => {
    const swiper = swiperInstance;
    if (!swiper?.autoplay || swiper.destroyed) {
      return;
    }

    const autoplay = swiper.autoplay;

    return scheduleDelayedAutoplay({
      autoplay,
      canStart: canStartAutoplay,
      isAutoplayUsable: () => !swiper.destroyed && swiper.autoplay === autoplay,
      startDelayMs,
    });
  }, [canStartAutoplay, isEnabled, startDelayMs, swiperInstance]);

  const onSwiper = useCallback((swiper: SwiperCore) => {
    if (!swiper.destroyed) {
      swiper.autoplay?.stop();
    }
    setSwiperInstance(swiper);
  }, []);

  const onDestroy = useCallback(() => {
    setSwiperInstance(null);
  }, []);

  return { onDestroy, onSwiper };
}
